/**
 * @file pages/Tokens.jsx
 * @description API Token management page.
 *
 * Features:
 *  - Lists all tokens with metadata (name, prefix, scopes, expiry, status)
 *  - Create button opens TokenCreateModal (one-time plaintext reveal)
 *  - Revoke action (admin only) soft-deletes the token
 *
 * Access: admin and operator roles only (enforced by RoleGuard in App.jsx).
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import DataTable        from '../components/DataTable';
import TokenCreateModal from '../components/TokenCreateModal';
import TokenRevealModal from '../components/TokenRevealModal';
import apiClient        from '../api/client';
import { listTokens, revokeToken, regenerateToken } from '../api/tokens';

/** Formats an ISO datetime string or returns 'Never' if null. */
const formatExpiry = (dt) =>
  dt ? new Date(dt).toLocaleString() : <span className="text-muted">Never</span>;

/**
 * Token list page component.
 */
export default function Tokens() {
  const queryClient = useQueryClient();

  /** Controls visibility of the create/edit modal; holds the token being edited, or null for create */
  const [showCreate, setShowCreate] = useState(false);
  const [editingToken, setEditingToken] = useState(null);

  /** Plaintext value from the most recent regenerate call, shown once in TokenRevealModal */
  const [revealedToken, setRevealedToken] = useState(null);

  // Regenerate is destructive to the old secret, so gate it behind the admin role
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn:  async () => (await apiClient.get('/api/users/me')).data,
    staleTime: 10 * 60 * 1000,
  });
  const isAdmin = currentUser?.role === 'admin';

  // Fetch the token list — refetch whenever the queryKey is invalidated
  const { data: tokens = [], isLoading, error } = useQuery({
    queryKey: ['tokens'],
    queryFn:  listTokens,
  });

  // Revoke mutation — invalidates the list on success
  const revokeMutation = useMutation({
    mutationFn: revokeToken,
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['tokens'] }),
  });

  // Regenerate mutation — shows the new plaintext value once on success
  const regenerateMutation = useMutation({
    mutationFn: regenerateToken,
    onSuccess: (data) => {
      setRevealedToken(data.token);
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  /**
   * Prompts for confirmation before revoking to prevent accidental clicks.
   * @param {Object} token - The token object to revoke.
   */
  const handleRevoke = (token) => {
    if (window.confirm(`Revoke token "${token.name}"? This cannot be undone.`)) {
      revokeMutation.mutate(token.id);
    }
  };

  /**
   * Confirms and regenerates a token's secret, invalidating the previous value.
   * @param {Object} token - The token object to regenerate.
   */
  const handleRegenerate = (token) => {
    if (window.confirm(`Regenerate the secret for "${token.name}"? The current value will stop working immediately.`)) {
      regenerateMutation.mutate(token.id);
    }
  };

  /** DataTable column definitions */
  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => (
        <div>
          <strong>{row.name}</strong>
          {row.description && (
            <div className="text-muted small">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'token_prefix',
      label: 'Prefix',
      render: (row) => (
        <code className="bg-light px-2 py-1 rounded">{row.token_prefix}…</code>
      ),
    },
    {
      key: 'scopes',
      label: 'Scopes',
      render: (row) =>
        row.scopes?.length > 0 ? (
          row.scopes.map((s) => (
            <span
              key={s}
              className="badge me-1"
              style={{ backgroundColor: 'var(--color-accent-2)', color: '#000' }}
            >
              {s}
            </span>
          ))
        ) : (
          <span className="text-muted small">None</span>
        ),
    },
    {
      key: 'expires_at',
      label: 'Expires',
      render: (row) => formatExpiry(row.expires_at),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        if (row.revoked) return <span className="badge bg-danger">Revoked</span>;
        if (row.expires_at && new Date(row.expires_at) < new Date()) {
          return <span className="badge bg-warning text-dark">Expired</span>;
        }
        return <span className="badge bg-success">Active</span>;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setEditingToken(row)}
            aria-label={`Edit token ${row.name}`}
          >
            <i className="bi bi-pencil" aria-hidden="true" />
          </button>

          {/* Regenerating the secret is destructive to the old value, so only admins get this action */}
          {isAdmin && !row.revoked && (
            <button
              className="btn btn-sm btn-outline-warning"
              onClick={() => handleRegenerate(row)}
              disabled={regenerateMutation.isPending}
              aria-label={`Regenerate token ${row.name}`}
              title="Regenerate secret"
            >
              <i className="bi bi-arrow-repeat" aria-hidden="true" />
            </button>
          )}

          {!row.revoked && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleRevoke(row)}
              disabled={revokeMutation.isPending}
              aria-label={`Revoke token ${row.name}`}
            >
              <i className="bi bi-x-circle" aria-hidden="true" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-content">
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h4 fw-bold mb-0" style={{ color: 'var(--color-primary)' }}>
          <i className="bi bi-key-fill me-2" aria-hidden="true" />
          API Tokens
        </h1>
        <button
          className="btn"
          style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
          onClick={() => setShowCreate(true)}
        >
          <i className="bi bi-plus-circle me-1" aria-hidden="true" />
          New Token
        </button>
      </div>

      {/* Token list */}
      <div className="card">
        <div className="card-body p-0">
          <DataTable
            columns={columns}
            data={tokens}
            isLoading={isLoading}
            error={error?.message}
            emptyMessage="No tokens found. Create one to get started."
            getRowKey={(row) => row.id}
          />
        </div>
      </div>

      {/* Mutation error banners */}
      {revokeMutation.isError && (
        <div className="alert alert-danger mt-3" role="alert">
          {revokeMutation.error?.message || 'Failed to revoke token.'}
        </div>
      )}
      {regenerateMutation.isError && (
        <div className="alert alert-danger mt-3" role="alert">
          {regenerateMutation.error?.message || 'Failed to regenerate token.'}
        </div>
      )}

      {/* Create / Edit modal */}
      <TokenCreateModal
        show={showCreate || Boolean(editingToken)}
        editToken={editingToken}
        onClose={() => { setShowCreate(false); setEditingToken(null); }}
        onCreated={() => { setShowCreate(false); setEditingToken(null); }}
      />

      {/* One-time reveal after a regenerate */}
      <TokenRevealModal token={revealedToken} onClose={() => setRevealedToken(null)} />
    </div>
  );
}
